import React, { useState, useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import axios from 'axios';
import GeneralUtilService from '~/utils/generalUtil';

import InputField from '~/components/atoms/InputField';
import SelectField from '~/components/atoms/SelectField';
import AutocompleteInputField from '~/components/atoms/AutocompleteInputField';
import CheckboxField from '~/components/atoms/CheckboxField';
import Button from '~/components/atoms/Button';
import useFormSubmit from '~/hooks/useFormSubmit';

import { getLonAndLat } from '../helpers/getLonAndLat';
import { calculateEstimate } from '../helpers/calculateEstimate';
import { pricingFormLabels } from '~/data/translations/pricingFormLabels';

const VisuallyHiddenField = styled.div`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  pointer-events: none;
`;

const firstnameKey = 'field1';
const lastnameKey = 'field2';
const emailKey = 'field3';
const phoneKey = 'field4';
const currentYear = new Date().getFullYear();

const validationSchema = Yup.object({
  firstname: Yup.string(),
  lastname: Yup.string(),
  email: Yup.string().email('Invalid email address'),
  phone: Yup.string(),
  number_of_bedrooms: Yup.string().required('Required'),
  property_address: Yup.string().required('Required'),
  of_properties: Yup.string().required('Required'),
  i_agree_to_receive_messages_via_whatsapp: Yup.boolean(),
  privacy: Yup.boolean().oneOf([true], 'This field must be checked'),
  spam: Yup.boolean().oneOf([false], ''),
  [`${firstnameKey}`]: Yup.string().required('Required'),
  [`${lastnameKey}`]: Yup.string().required('Required'),
  [`${emailKey}`]: Yup.string().email('Invalid email address').required('Required'),
  [`${phoneKey}`]: Yup.string().required('Required'),
});

export default function PricingForm({ setIsSubmitted, className, language = 'en' }) {
  // -------------------------------------------------------------------------
  // MONITOR MODE (added for the automated submit-monitor).
  // Active ONLY when the URL has ?monitor=1. For a normal visitor this is
  // always false, so the live lead funnel behaves exactly as before.
  // When active it: uses hardcoded London Bridge coordinates (so the monitor
  // does not depend on Google Places autocomplete), and downstream it skips
  // the HubSpot submission, the ad tracking pixels, and the results-page PDF
  // email (via sendToEmail:false) so a monitor run creates no lead, no email
  // and no tracking event.
  // NOTE: review with Antanas before this stays on production.
  // -------------------------------------------------------------------------
  const isMonitor =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('monitor') === '1';

  // Hardcoded London Bridge coords used ONLY in monitor mode.
  const MONITOR_COORDS = { latitude: 51.5095, longitude: -0.0872 };

  // ---------------------------------------------------------------------------
  // Rentalizer estimate call (City Relay portal endpoints)
  //
  // NOTE: The `future` block returned by the API is a rolling 12-month window
  // (e.g. Aug 2026 -> Jul 2027). The forEach below rewrites the YEAR of every
  // future metric to the CURRENT year, so the 12 points line up with a clean
  // Jan..Dec calendar and the chart's fixed month labels. This is intentional:
  // it is what makes the chart look like a single calendar year with a full
  // occupancy line. We deliberately do NOT mix in `historical` data — historical
  // metrics use a different revenue field (`revenue_valuation`) on a different
  // scale and carry no occupancy, so stitching them in produces broken charts.
  // ---------------------------------------------------------------------------
  async function getData(latitude, longitude, bedrooms, sRef, token) {
    const res = await fetch(
      `https://driveportal.cityrelaysolutions.com:443/public/getRentalizerEstimate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sRef,
          token,
          bedrooms: bedrooms,
          latitude: latitude,
          longitude: longitude,
          currency: 'gbp',
        }),
      }
    );

    const data = await res.json();

    // Rewrite each future metric's year to the current year so the 12 rolling
    // months map onto Jan..Dec of this year (this is where the chart's calendar
    // alignment comes from).
    data?.map?.payload?.stats?.future?.metrics?.forEach((metric) => {
      const dateYearMatch = metric?.date?.match(/\d{4}/);
      if (dateYearMatch && dateYearMatch[0] !== String(currentYear)) {
        metric.date = metric.date.replace(dateYearMatch[0], String(currentYear));
      }
    });

    return data;
  }

  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const valuationRef = useRef({ sRef: null, token: null });
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedFN, setSelectedFN] = useState('');
  const [selectedLN, setSelectedLN] = useState('');
  const [selectedNumBedrooms, setNumBedrooms] = useState('1');
  const [selectedNumPortfolio, setNumPortfolio] = useState('1');
  const [isAgreeWhatsAppChecked, setAgreeWhatsAppChecked] = useState(false);
  const [isPrivacyChecked, setPrivacyChecked] = useState(false);
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false);
  const [valuationFormContext, setValuationFormContext] = useState(null);

  const handleSelectAddress = (value) => {
    setSelectedAddress(value);
  };

  const handleSelectPhone = (event) => {
    const value = event.target.value;
    setSelectedPhone(value);
  };

  const handleSelectFN = (event) => {
    const value = event.target.value;
    setSelectedFN(value);
  };

  const handleSelectLN = (event) => {
    const value = event.target.value;
    setSelectedLN(value);
  };

  const handleSelectEmail = (event) => {
    const value = event.target.value;
    setSelectedEmail(value);
  };

  const handleSelectBedrooms = (event) => {
    setNumBedrooms(event.target.value);
  };

  const handleSelectNumPortfolio = (event) => {
    setNumPortfolio(event.target.value);
  };

  const handlePrivacyChange = (event) => {
    setPrivacyChecked(event.target.checked);
  };

  const handleAgreeWhatsApp = (event) => {
    setAgreeWhatsAppChecked(event.target.checked);
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isValidPhoneNumber = (phone) => {
    const regex = /^\+?(\d{1,3})?[-.\s]?((\d{1,4}[-.\s]?){1,3}\d{1,4})$/;
    return regex.test(phone);
  };

  const checkIfAllFieldsFilled = () => {
    const emailIsValid = isValidEmail(selectedEmail);
    const phoneIsValid = isValidPhoneNumber(selectedPhone);

    const allFieldsFilled =
      selectedAddress &&
      selectedAddress.label != 'UK' &&
      selectedAddress.label != 'London' &&
      selectedAddress.label != 'London, UK' &&
      selectedPhone &&
      emailIsValid &&
      phoneIsValid &&
      selectedFN &&
      selectedLN &&
      selectedNumBedrooms &&
      selectedNumPortfolio &&
      isPrivacyChecked;

    setIsAllFieldsFilled(allFieldsFilled);

    if (selectedAddress != null) {
      if (
        selectedAddress.label == 'London, UK' ||
        selectedAddress.label == 'London' ||
        selectedAddress.label == 'UK'
      ) {
        alert('Please provide more details in the property address field!');
        return;
      }
    }
  };

  useEffect(() => {
    checkIfAllFieldsFilled();
  }, [
    selectedAddress,
    selectedEmail,
    selectedPhone,
    selectedFN,
    selectedLN,
    selectedNumBedrooms,
    selectedNumPortfolio,
    isAgreeWhatsAppChecked,
    isPrivacyChecked,
  ]);

  const cityName =
    (typeof window !== 'undefined' &&
      (window.location.pathname.includes('/fr/') || window.location.pathname.includes('/es'))) ||
    language === 'fr' ||
    language === 'es'
      ? 'not_london'
      : 'london';

  const formId =
    cityName === 'london'
      ? '070c7fb5-f573-4378-b9fb-0b0c1be1551b'
      : '29a15bb5-3c6b-4ea8-8e18-6a0a32e2270c';

  const { post, response } = useFormSubmit({
    portalId: '7026924',
    formId,
    setIsSubmitted,
  });

  let getEstimate;

  if (cityName == 'london') {
    getEstimate = async ({ address, bedrooms }) => {
      if (!address) return false;

      // MONITOR MODE: skip Google Places and use hardcoded coords.
      const location = isMonitor ? MONITOR_COORDS : await getLonAndLat(address);
      const { latitude, longitude } = location;

      try {
        const data = await getData(
          latitude,
          longitude,
          bedrooms,
          valuationRef.current.sRef,
          valuationRef.current.token
        );
        const future = data?.map?.payload?.stats?.future;
        return future;
      } catch (error) {
        console.error(error);
        return false;
      }
    };
  } else {
    getEstimate = async ({ address, bedrooms }) => {
      if (!address) return false;

      // MONITOR MODE: skip Google Places and use hardcoded coords.
      const location = isMonitor ? MONITOR_COORDS : await getLonAndLat(address);
      const { latitude, longitude } = location;

      try {
        const data = await getData(
          latitude,
          longitude,
          bedrooms,
          valuationRef.current.sRef,
          valuationRef.current.token
        );
        const metrics = data?.map?.payload?.stats?.future?.metrics;
        return calculateEstimate(metrics);
      } catch (error) {
        console.error(error);
        return false;
      }
    };
  }

  const labels = pricingFormLabels[language];

  return (
    <Formik
        initialValues={{
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          property_address: '',
          number_of_bedrooms: '1',
          is_your_property_available_365_days_a_year_: false,
          i_agree_to_receive_messages_via_whatsapp: false,
          of_properties: '1',
          privacy: false,
          spam: false,
          [`${firstnameKey}`]: '',
          [`${lastnameKey}`]: '',
          [`${emailKey}`]: '',
          [`${phoneKey}`]: '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, formikProps) => {
          if (submitLock.current) return;

          submitLock.current = true;
          setSubmitting(true);
          setErrorMessage('');

          try {
            const sRef = GeneralUtilService.generateRandomString(20);

            try {
              const contextRes = await fetch(
                'https://driveportal.cityrelaysolutions.com/public/getValuationFormContext',
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sRef }),
                }
              );

              const contextData = await contextRes.json();

              valuationRef.current = { sRef, token: contextData?.map?.value ?? null };

              setValuationFormContext({
                sRef,
                token: contextData?.map?.value ?? null,
                expiresInSec: contextData?.map?.sec ?? null,
              });
            } catch (contextErr) {}

            const { firstname, lastname, email, phone } = values;
            const isSpam = firstname || lastname || email;

            const blockedPhoneNumbers = [
              '0791727676', '07916126252', '07917127671', '07917127673', '07917127676',
              '07917127678', '079171227623', '079171276764', '079171276776', '07917127672',
              '+447917127672', '+4407917127672', '+440791727676', '+4407916126252', '+4407917127671',
              '+4407917127673', '+4407917127676', '+4407917127678', '+44079171227623',
              '+44079171276764', '+44079171276776', '+44791727676', '+447916126252',
              '+447917127671', '+447917127673', '+447917127676', '+447917127678',
              '+4479171227623', '+4479171276764', '+4479171276776', '07985854714', '+447985854714',
            ];

            const blockedEmails = [
              'djd@hattonandddvernon.co.uk',
              'djd@hattonandvern.co.uk',
              'djd@hattonandvernin.co.uk',
              'djd@hattonandvernon.co.uk',
              'djd@hattonandvernon.com',
              'djd@hattonanvernon.com',
              'djd@hattonandvenron.com',
              'djdd@hattonandvernon.com',
              'djd@hattonandvernonss.com',
              'jgjghjh@gmail.com',
            ];

            const userEnteredPhone = values[phoneKey];
            const userEnteredEmail = values[emailKey];
            const userEnteredPropertyAddress = values['property_address'];
            const userEnteredFirstName = values[firstnameKey];
            const userEnteredLastName = values[lastnameKey];

            if (
              !userEnteredPhone ||
              !userEnteredEmail ||
              !userEnteredPropertyAddress ||
              !userEnteredFirstName ||
              !userEnteredLastName ||
              blockedPhoneNumbers.includes(userEnteredPhone) ||
              blockedEmails.includes(userEnteredEmail) ||
              userEnteredEmail.includes('@hattonan') ||
              userEnteredPropertyAddress.trim() === '' ||
              userEnteredPropertyAddress.trim() == 'London, UK' ||
              userEnteredPropertyAddress.trim() == 'London' ||
              userEnteredPropertyAddress.trim() == 'UK' ||
              userEnteredFirstName.trim() === '' ||
              userEnteredLastName.trim() === '' ||
              userEnteredPhone.trim() === '' ||
              userEnteredEmail.trim() === ''
            ) {
              if (
                userEnteredPropertyAddress.trim() == 'London, UK' ||
                userEnteredPropertyAddress.trim() == 'London' ||
                userEnteredPropertyAddress.trim() == 'UK'
              ) {
                formikProps.setFieldError('property_address', 'Please provide more details');
              }
              return;
            } else if (isSpam) {
              formikProps.resetForm();
              return;
            } else {
              let estimate = '';
              let occupancyRevenueByMonth = null;
              let annual_revenue = 0;
              let occupancy = 0;
              let sendToEmail = false;

              if (cityName == 'not_london') {
                estimate = await getEstimate({
                  address: values.property_address,
                  bedrooms: values.number_of_bedrooms,
                });
              } else {
                const api_future_data = await getEstimate({
                  address: values.property_address,
                  bedrooms: values.number_of_bedrooms,
                });

                estimate = calculateEstimate(api_future_data?.metrics);

                // Chart data + summary cards all come from the (year-rewritten)
                // future window. No historical stitching. Guarded so an empty
                // response can't crash submit before navigation.
                const futureMetrics = api_future_data?.metrics;
                occupancyRevenueByMonth = Array.isArray(futureMetrics)
                  ? futureMetrics.map(({ date, occupancy, revenue }) => ({
                      date,
                      occupancy,
                      revenue,
                    }))
                  : [];
                occupancy = api_future_data?.summary?.occupancy;
                annual_revenue = api_future_data?.summary?.revenue;
                sendToEmail = true;
              }

              const valuesToSubmit = { ...values };

              if (estimate) {
                valuesToSubmit['estimate_min'] = Math.round((estimate * 1.1) / 10) * 10;
              }

              valuesToSubmit['firstname'] = values[firstnameKey];
              valuesToSubmit['lastname'] = values[lastnameKey];
              valuesToSubmit['email'] = values[emailKey];
              valuesToSubmit['phone'] = values[phoneKey];
              delete valuesToSubmit['privacy'];
              delete valuesToSubmit['spam'];
              delete valuesToSubmit[firstnameKey];
              delete valuesToSubmit[lastnameKey];
              delete valuesToSubmit[emailKey];
              delete valuesToSubmit[phoneKey];

              window.dataLayer = window.dataLayer || [];

              if (
                valuesToSubmit.email != '' &&
                valuesToSubmit.firstname != '' &&
                valuesToSubmit.lastname != '' &&
                valuesToSubmit.property_address != '' &&
                valuesToSubmit.phone != '' &&
                valuesToSubmit.of_properties != '' &&
                valuesToSubmit.number_of_bedrooms != ''
              ) {
                // MONITOR MODE: do NOT create a real HubSpot lead.
                if (!isMonitor) {
                  await post(valuesToSubmit);
                }

                if (response?.data?.status === 'error') {
                  const errorHeading = response?.data?.message;
                  const messages = response.data.errors.map(({ message }) => message).join('<br/>');
                  setErrorMessage(errorHeading + '<br /><br />' + messages);
                } else {
                  const submissionId = GeneralUtilService.generateRandomString(12, true);

                  if (cityName == 'london') {
                    const eventId = `rental_estimate_submitted-${GeneralUtilService.generateRandomString(10, true)}`;

                    // OpenAI Ads — browser pixel (skipped in monitor mode)
                    if (!isMonitor && typeof window !== 'undefined' && typeof window.oaiq === 'function') {
                      window.oaiq(
                        'measure',
                        'custom',
                        { type: 'custom' },
                        { custom_event_name: 'rental_estimate_submitted', event_id: eventId }
                      );
                    }

                    // OpenAI Ads — server-side (skipped in monitor mode)
                    if (!isMonitor) {
                      try {
                        fetch('/.netlify/functions/openai-ads-event', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            custom_event_name: 'rental_estimate_submitted',
                            event_id: eventId,
                            source_url: window.location.href,
                          }),
                        });
                      } catch (e) {}
                    }

                    navigate('/pricing/result/', {
                      state: {
                        ...values,
                        estimate,
                        language,
                        cityName,
                        occupancy,
                        annual_revenue,
                        occupancyRevenueByMonth,
                        // MONITOR MODE: force false so the results page does NOT
                        // email a PDF to Akim/Dominique.
                        sendToEmail: isMonitor ? false : sendToEmail,
                        fromPricingForm: true,
                        submissionId,
                      },
                    });
                  } else {
                    navigate('/pricing/result/', {
                      state: {
                        ...values,
                        estimate,
                        language,
                        cityName,
                        sendToEmail: isMonitor ? false : sendToEmail,
                        fromPricingForm: true,
                        submissionId,
                      },
                    });
                  }
                }
              }
            }
          } finally {
            submitLock.current = false;
            setSubmitting(false);
          }
        }}
      >
        {(props) => (
          <Form id="pricing-form" className="grid grid-cols-12 gap-x-4 gap-y-8">
            <VisuallyHiddenField>
              <InputField divclassname="col-span-12" name="firstname" type="text" label={labels.firstname} value={selectedFN} />
              <InputField divclassname="col-span-12" name="lastname" type="text" label={labels.lastname} value={selectedLN} />
              <InputField divclassname="col-span-12" name="email" type="email" label={labels.email} value={selectedEmail} />
              <InputField divclassname="col-span-12" name="phone" type="text" label={labels.phone} value={selectedPhone} />
            </VisuallyHiddenField>

            <InputField onChange={handleSelectFN} value={selectedFN} divclassname="col-span-12 sm:col-span-6" name={firstnameKey} type="text" autoComplete="off" label={labels.firstname} />
            <InputField onChange={handleSelectLN} value={selectedLN} divclassname="col-span-12 sm:col-span-6" name={lastnameKey} type="text" autoComplete="off" label={labels.lastname} />
            <InputField onChange={handleSelectEmail} value={selectedEmail} divclassname="col-span-12 sm:col-span-6" name={emailKey} type="email" autoComplete="off" label={labels.email} />
            <InputField onChange={handleSelectPhone} value={selectedPhone} divclassname="col-span-12 sm:col-span-6" name={phoneKey} type="text" autoComplete="off" label={labels.phone} />

            <AutocompleteInputField
              label={labels.property_address}
              divclassname="col-span-12"
              name="property_address"
              type="text"
              onSelect={handleSelectAddress}
            />

            <SelectField
              onChange={handleSelectBedrooms}
              value={selectedNumBedrooms}
              divclassname="col-span-12 sm:col-span-6"
              name="number_of_bedrooms"
              type="text"
              label={labels.number_of_bedrooms}
              options={[
                { label: 'Studio', value: '0' },
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4', value: '4' },
                { label: '5+', value: '5' },
              ]}
            />

            <SelectField
              onChange={handleSelectNumPortfolio}
              value={selectedNumPortfolio}
              divclassname="col-span-12 sm:col-span-6"
              name="of_properties"
              type="text"
              label={labels.of_properties}
              options={[
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4-10', value: '4 - 10' },
                { label: '11-20', value: '11 - 20' },
                { label: '20+', value: '20+' },
              ]}
            />

            <CheckboxField
              divclassname="col-span-12"
              name="is_your_property_available_365_days_a_year_"
              label={labels.is_your_property_available_365_days_a_year_}
            />

            <CheckboxField
              divclassname="col-span-12"
              name="i_agree_to_receive_messages_via_whatsapp"
              label={labels.i_agree_to_receive_messages_via_whatsapp}
              onChange={handleAgreeWhatsApp}
              checked={isAgreeWhatsAppChecked}
            />

            <CheckboxField
              divclassname="col-span-12"
              name="privacy"
              label={labels.privacy}
              onChange={handlePrivacyChange}
              checked={isPrivacyChecked}
            />

            <VisuallyHiddenField>
              <CheckboxField divclassname="col-span-12" name="spam" />
            </VisuallyHiddenField>

            <div className="col-span-12">
              <div className="w-full">
                <Button
                  disabled={(!isAllFieldsFilled && !isMonitor) || submitting}
                  title={submitting ? 'Submitting...' : labels.results}
                  type="tertiary"
                  className={`w-full ${(!(props.isValid && props.dirty) || submitting) && 'hover:cursor-default'}`}
                  submitting={submitting}
                />
              </div>

              {errorMessage.length > 0 && (
                <div
                  className="max-w-md mt-8 text-sm text-error"
                  dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
              )}
            </div>
          </Form>
        )}
      </Formik>
  );
}